import { filter, includes, isEmpty, isNil, isString, size, toLower, toNumber, toString, get, join } from 'lodash'
import dayjs from 'dayjs'
import isSameOrBefore from 'dayjs/plugin/isSameOrBefore'
import isSameOrAfter from 'dayjs/plugin/isSameOrAfter'
import utc from 'dayjs/plugin/utc'
import { VOLUMES_ORDER } from '@/utils/constants'

dayjs.extend(isSameOrBefore)
dayjs.extend(isSameOrAfter)
dayjs.extend(utc)

export function windowUrl(blob) {
    return window.URL.createObjectURL(blob)
}

export function documentLink() {
    return document.createElement('a')
}

export function countChildren(node, isDeleting, state) {
    const undeletedChildren = isDeleting ? filter(node.children, child => child.action !== 'delete') : filter(node.children, child => child.action === 'delete')
    const createdChildren = filter(node.children, child => includes(state.createdSegmentKeys, child.key))
    const updatedChildren = filter(node.children, child => includes(state.updatedSegmentIds, child.id))

    let _countDelete = size(undeletedChildren)
    let _countCreate = size(createdChildren)
    let _countUpdate = size(updatedChildren)

    for (const child of undeletedChildren) {
        if (!isEmpty(child.children)) {
            const _counts = countChildren(child, isDeleting, state)
            _countDelete += _counts[0]
            _countCreate += _counts[1]
            _countUpdate += _counts[2]
        }
    }
    return [_countDelete, _countCreate, _countUpdate]
}

export function errorHandler(error, commit) {
    let errorMessage
    const apiError = get(error, 'apiError', null)
    const _errorMessage = get(error, 'message', null)
    if (isNil(apiError)) {
        errorMessage = isNil(_errorMessage) ? error : _errorMessage
    } else {
        errorMessage = isNil(get(error, 'apiError.message', null)) ? error.apiError.statusText : error.apiError.message
    }
    if (commit) {
        commit(
            'notifications/setAddedNotification',
            {
                type: 'error',
                message: errorMessage
            },
            { root: true }
        )
    }
}

/**
 *
 * @param {String} value A date
 * @param {Boolean} fulldate Return with hour
 * @param {Boolean} fromUtc Parse from utc source
 * @returns {String}
 */
export function formatDate(value, fulldate = false, fromUtc = false) {
    // handle empty date
    if (value === '0000-00-00 00:00:00') {
        return 'Invalid date'
    }

    const date = fromUtc ? dayjs.utc(value).local() : dayjs(value)
    const format = fulldate ? 'YYYY-MM-DD HH:mm' : 'YYYY-MM-DD'
    return date.format(format)
}

export function formatDateVerbose(value) {
    if (value) {
        return dayjs(value).format('DD/MM/YYYY')
    }
}

export function totalTransfers(transferList) {
    let total = 0
    for (const tKey in transferList) {
        const transfer = transferList[tKey]
        total += size(transfer)
    }
    return total
}

export function totalItemsByStatus(data, status) {
    if (isNil(status) || status === '' || isNil(data) || isEmpty(data)) return 'no data available'
    const totalItems = []
    for (const _data of data) {
        if (_data.status === status) totalItems.push(_data)
    }
    return size(totalItems)
}

export function dataToSearchedItems(data, searchValue, searchTarget) {
    return filter(data, elem => {
        if (searchTarget === 'label' || searchTarget === '') {
            if (includes(toLower(elem.label), toLower(searchValue))) {
                return true
            }
        }
        if (searchTarget === 'id' || searchTarget === '') {
            if (includes(toString(elem.id), searchValue)) {
                return true
            }
        }
        if (searchTarget === 'tag' || searchTarget === '') {
            if (!elem.tags || elem.tags.length === 0) return false
            const tagLabels = elem.tags.map(tag => tag.label)
            const foundPattern = filter(tagLabels, label => {
                return includes(toLower(label), toLower(searchValue))
            })
            return !isEmpty(foundPattern)
        }
        return false
    })
}

export function dataToFilteredItems(data, keyStart, keyEnd, dates, targets, dataType, partnersFilter, transfers) {
    const createdDate = dates.createdDate
    const startDate = dates.startDate
    const endDate = dates.endDate

    const bootstrapTarget = targets.bootstrap
    const statusTarget = targets.status
    const typeTarget = targets.type
    const sharedTarget = targets.shared

    return filter(data, elem => {
        // filter on targets
        if (bootstrapTarget !== '' && elem.bootstrap != bootstrapTarget) return false
        if (statusTarget !== '' && elem.status !== statusTarget) return false
        if (typeTarget !== '' && elem.type !== typeTarget) return false
        if (sharedTarget !== '') {
            if (sharedTarget === 'yes' && isNil(elem.sharedAudience)) {
                return false
            } else if (sharedTarget === 'no' && !isNil(elem.sharedAudience)) {
                return false
            }
        }

        // filter on date created
        if (!isNil(createdDate.from)) {
            const _dateKey = 'created_at'

            const createdDateFrom = formatDate(createdDate.from)
            const createdDateTo = formatDate(createdDate.to)

            if (createdDate.operator === 'equal' && !dayjs(elem[_dateKey]).isSame(createdDateFrom, 'day')) {
                return false
            } else if (createdDate.operator === 'before' && !dayjs(elem[_dateKey]).isSameOrBefore(createdDateFrom, 'day')) {
                return false
            } else if (
                (createdDate.operator === 'after' || createdDate.operator === 'period') &&
                !dayjs(elem[_dateKey]).isBetween(createdDateFrom, createdDateTo, 'day', [])
            ) {
                return false
            }
        }
        // filter on dates start/activation
        if (!isNil(startDate.from)) {
            const _dateKey = keyStart === 'Activation' ? 'start_date' : 'createdAt'
            const startDateFrom = formatDate(startDate.from)
            const startDateTo = formatDate(startDate.to)

            if (startDate.operator === 'equal' && !dayjs(elem[_dateKey]).isSame(startDateFrom, 'day')) {
                return false
            } else if (startDate.operator === 'before' && !dayjs(elem[_dateKey]).isSameOrBefore(startDateFrom, 'day')) {
                return false
            } else if (startDate.operator === 'after' && !dayjs(elem[_dateKey]).isSameOrAfter(startDateFrom, 'day')) {
                return false
            } else if (startDate.operator === 'period' && !dayjs(elem[_dateKey]).isBetween(startDateFrom, startDateTo, 'day', [])
            ) {
                return false
            }
        }
        // check on end or update date according to key param
        if (!isNil(endDate.from)) {
            let _dateKey = ''
            if (dataType === 'cclones') _dateKey = 'end_date'
            else _dateKey = keyEnd === 'Update' ? 'updatedAt' : 'endDate'

            const endDateFrom = formatDate(endDate.from)
            const endDateTo = formatDate(endDate.to)

            if (endDate.operator === 'equal' && !dayjs(elem[_dateKey]).isSame(endDateFrom, 'day')) {
                return false
            } else if (endDate.operator === 'before' && !dayjs(elem[_dateKey]).isSameOrBefore(endDateFrom, 'day')) {
                return false
            } else if (endDate.operator === 'after' && !dayjs(elem[_dateKey]).isSameOrAfter(endDateFrom, 'day')) {
                return false
            } else if (endDate.operator === 'period' && !isNil(endDate.to) && !dayjs(elem[_dateKey]).isBetween(endDateFrom, endDateTo, 'day', [])) {
                return false
            }
        }

        if (!isEmpty(partnersFilter)) {
            const transfersList = transfers[elem.id]
            if (transfersList) {
                let hasOneOfTheRequiredDsp = false
                for (const transfer of transfersList) {
                    if (partnersFilter.includes(transfer.partner)) hasOneOfTheRequiredDsp = true
                    else if (partnersFilter.includes('Pixel URL') && !isEmpty(transfer.pixel_url)) hasOneOfTheRequiredDsp = true
                }
                return hasOneOfTheRequiredDsp
            } else return false
        }

        return true
    })
}

export function dataToSortedItems(data, sorting) {
    const sortOrder = sorting.order === 'asc' ? 1 : -1
    const sortKey = isString(sorting.key) ? sorting.key : 'id'

    return data.sort((a, b) => {
        a = sortKey != 'id' ? String(a[sortKey]).toLowerCase() : parseInt(a[sortKey])
        b = sortKey != 'id' ? String(b[sortKey]).toLowerCase() : parseInt(b[sortKey])
        return (a === b ? 0 : a > b ? 1 : -1) * sortOrder
    })
}

export function dataToPaginateItems(data, pagination) {
    return data.slice(parseInt((pagination.currentPage - 1) * pagination.rowPerPage), parseInt(pagination.currentPage * pagination.rowPerPage))
}

//set transfer params when creating transfers
export function formatTransferParams(audience, transfer, techno) {
    const commonParams = {
        audience_id: audience.id,
        email: transfer.email,
        cc: join(transfer.email_cc, ','),
        bootstrap: transfer.bootstrap,
        bootstrap_status: transfer.bootstrap_status,
        label: transfer.label,
        end_date: dayjs(transfer.end_date).format('YYYY-MM-DD')
    }
    let _transfer = {}
    if (techno === 'media') {
        _transfer = {
            ...commonParams,
            dsp_id: transfer.dspId,
            cookie_split: transfer.cookieSplit,
            delivering_type: transfer.deviceMode,
            accounts_dsp_id: get(transfer, 'accountDspId', '')
        }
        if (get(transfer, 'cpm', null)) _transfer = { ..._transfer, cpm: transfer.cpm }
        if (get(transfer, 'media_rate', null)) _transfer = { ..._transfer, media_rate: transfer.media_rate }
        if (get(transfer, 'dsp_segment_id', null)) _transfer = { ..._transfer, dsp_segment_id: transfer.dsp_segment_id }
    } else if (techno === 'pixel') {
        _transfer = {
            ...commonParams,
            accounts_dsp_id: 0,
            pixel_url: transfer.url,
            delivering_type: 'Desktop identifier'
        }
    }

    if (transfer.reference_id) {
        _transfer.reference_id = transfer.reference_id
        _transfer.cpm = transfer.cpm
    }

    return _transfer
}

/**
 * @param {Number|String} number
 * @returns {String}
 */
export const formatNumber = number => toNumber(number).toLocaleString('en-US')

export const formatNumberIntl = number => {
    const formatter = Intl.NumberFormat('en', { notation: 'compact' })
    return formatter.format(number)
}

export const validateTags = tags => {
    for (const tag in tags) {
        // allows dashes, underscores, digits, letters
        const isValid = RegExp(/^[a-z0-9-_]+$/i).test(tags[tag]) && tags[tag] !== ''
        if (!isValid) return false
    }
    return true
}

export const parseFilterParams = value => {
    if (value === 'true' || value === 'false') {
        value = JSON.parse(value)
    }
    if (value === '') {
        value = null
    }
    return value
}

export const stringifyFilterParams = value => {
    if (typeof value == 'boolean') {
        return value ? 'true' : 'false'
    }
    return value
}

export const objRemoveEmpty = obj => {
    return Object.fromEntries(Object.entries(obj).filter(([_, v]) => v != null))
}

export const weboAppLink = (name, domain, accountId) => {
    let hostName = name
    let env = ''
    if (location.hostname.includes('localhost') || location.hostname.includes('dev')) {
        env = 'dev'
        hostName = `${name}-${env}`
    }
    if (location.hostname.includes('preprod')) {
        env = 'preprod'
        hostName = `${name}-${env}`
    }

    const redirect = name === 'admin' ? '': name === 'datahub' ? 'homepage' : `redirect/${accountId}`

    if (name === 'admin' || name === 'app') {
        domain = 'weborama.com'
    }

    return `${location.protocol}//${hostName}.${domain}/${redirect}`
}

export const getTokenUserEmail = ($keycloak) => {
    const portalEmail = get($keycloak, 'tokenParsed.portal_email', '')
    const email = get($keycloak, 'tokenParsed.email', '')
    return portalEmail || email
}

export const totalVolume = ({ volumes, volume, type }) => {
    if (isNil(volumes)) return 'No volume'

    let total = null
    // ID Based
    if (['idbased', 'xdevice'].includes(type)) {
        total = get(type === 'xdevice' ? volumes.total_xdevice : volumes , `${volume}`)

        if (total !== undefined && total !== null) {
            return formatNumber(total)
        }
    }

    return 'N/A'
}

export const orderVolumes = (volumeName) => {
    if (VOLUMES_ORDER[volumeName]) {
        const volumes = { ...VOLUMES_ORDER[volumeName] }
        const volumeOrder = Object.keys(volumes).sort((a, b) => volumes[a] > volumes[b] ? 1 : volumes[a] < volumes[b] ? -1 : 0)
        return volumeOrder
    }
    // eslint-disable-next-line no-console
    console.error(`helper::orderVolumes "${volumeName}" doen't exist !`)
    return []
}

// export const totalVolume2 = ({ volumes, volume, type }) => {
//     if (isNil(volumes)) return 'No volume'

//     const getTypeValue = {
//         idbased: () => get(volumes, `${volume}`),
//         xdevice: () => get(volumes.total_xdevice, `${volume}`)
//     }

//     return getTypeValue[type] === undefined ? 'N/A': formatNumber(getTypeValue[type]())
// }
